import {
  Icon,
  IconColor,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { OcppLogMessageFormatted } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/logger/ocpp-logs-messsages.interface';
import {
  isOcppLogCallResultMessage,
  isOcppLogCallResultMessageAuthorize,
  isOcppLogCallResultMessageBootNotification,
  isOcppLogCallResultMessageHeartbeat,
  isOcppLogCallResultMessageRemoteStartTransaction,
  isOcppLogCallResultMessageStartTransaction,
  isOcppLogCallResultMessageStatusNotification,
  isOcppLogCallResultMessageStopTransaction,
  isOcppLogCallResultOrErrorMessage,
  isOcppLogCallResultSendLocalList,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/logger/ocpp-logs-messsages.utils';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';

import * as css from './log-item-title.module.scss';

const getLogDisplayInfo = (
  log: OcppLogMessageFormatted,
  noInfoMessage: string,
): {
  iconName: string;
  iconColor: IconColor;
  tag?: { label: string; className: string };
} => {
  const { message } = log;

  if (!isOcppLogCallResultMessage(message)) {
    return {
      iconName: 'electric-car-2',
      iconColor: IconColor.Primary,
      tag: undefined,
    };
  }

  if (isOcppLogCallResultMessageAuthorize(message)) {
    if (!message.result.idTagInfo) {
      return {
        iconName: 'annotation-dots',
        iconColor: IconColor.Warning,
        tag: { label: noInfoMessage, className: 'bg-surface-warning' },
      };
    }

    return {
      iconName: 'annotation-dots',
      iconColor:
        message.result.idTagInfo.status === 'Accepted'
          ? IconColor.Success
          : IconColor.Error,
      tag: {
        label: message.result.idTagInfo.status ?? '',
        className:
          message.result.idTagInfo.status === 'Accepted'
            ? 'bg-surface-success'
            : 'bg-surface-error',
      },
    };
  }

  if (isOcppLogCallResultMessageBootNotification(message)) {
    return {
      iconName: 'annotation-dots',
      iconColor: IconColor.Primary,
      tag: undefined,
    };
  }

  if (isOcppLogCallResultMessageHeartbeat(message)) {
    return {
      iconName: 'activity-heart',
      iconColor: IconColor.Primary,
      tag: undefined,
    };
  }

  if (isOcppLogCallResultMessageStartTransaction(message)) {
    if (!message.result.idTagInfo) {
      return {
        iconName: 'annotation-dots',
        iconColor: IconColor.Warning,
        tag: { label: noInfoMessage, className: 'bg-surface-warning' },
      };
    }

    return {
      iconName: 'annotation-dots',
      iconColor:
        message.result.idTagInfo.status === 'Invalid'
          ? IconColor.Error
          : IconColor.Success,
      tag: {
        label: message.result.idTagInfo.status,
        className:
          message.result.idTagInfo.status === 'Invalid'
            ? 'bg-surface-error'
            : 'bg-surface-success',
      },
    };
  }

  if (isOcppLogCallResultMessageRemoteStartTransaction(message)) {
    if (!message.result.status) {
      return {
        iconName: 'annotation-dots',
        iconColor: IconColor.Warning,
        tag: { label: noInfoMessage, className: 'bg-surface-warning' },
      };
    }

    return {
      iconName: 'annotation-dots',
      iconColor:
        message.result.status === 'Rejected'
          ? IconColor.Error
          : IconColor.Success,
      tag: {
        label: message.result.status,
        className:
          message.result.status === 'Rejected'
            ? 'bg-surface-error'
            : 'bg-surface-success',
      },
    };
  }

  if (isOcppLogCallResultMessageStatusNotification(message)) {
    return {
      iconName: 'wire',
      iconColor:
        message.parameters.status === 'Unavailable'
          ? IconColor.Secondary
          : message.parameters.status === 'Available'
            ? IconColor.Success
            : message.parameters.status === 'Preparing'
              ? IconColor.Secondary
              : message.parameters.status === 'Charging'
                ? IconColor.Charging
                : IconColor.Primary,
      tag: {
        label: message.parameters.status,
        className:
          message.parameters.status === 'Unavailable'
            ? 'bg-surface-secondary'
            : message.parameters.status === 'Available'
              ? 'bg-surface-success'
              : message.parameters.status === 'Preparing'
                ? 'bg-surface-secondary'
                : message.parameters.status === 'Charging'
                  ? 'bg-surface-charging'
                  : 'bg-surface-primary',
      },
    };
  }

  if (isOcppLogCallResultMessageStopTransaction(message)) {
    if (!message.result.idTagInfo) {
      return {
        iconName: 'annotation-dots',
        iconColor: IconColor.Warning,
        tag: { label: noInfoMessage, className: 'bg-surface-warning' },
      };
    }

    return {
      iconName: 'annotation-dots',
      iconColor:
        message.result.idTagInfo.status === 'Accepted'
          ? IconColor.Success
          : IconColor.Error,
      tag: {
        label: message.result.idTagInfo.status,
        className:
          message.result.idTagInfo.status === 'Accepted'
            ? 'bg-surface-success'
            : 'bg-surface-error',
      },
    };
  }

  if (isOcppLogCallResultSendLocalList(message)) {
    if (!message.result.status) {
      return {
        iconName: 'annotation-dots',
        iconColor: IconColor.Warning,
        tag: { label: noInfoMessage, className: 'bg-surface-warning' },
      };
    }

    return {
      iconName: 'annotation-dots',
      iconColor:
        message.result.status === 'Accepted'
          ? IconColor.Success
          : message.result.status === 'NotSupported' ||
              message.result.status === 'VersionMismatch'
            ? IconColor.Warning
            : IconColor.Error,
      tag: {
        label: message.result.status,
        className:
          message.result.status === 'Accepted'
            ? 'bg-surface-success'
            : message.result.status === 'NotSupported' ||
                message.result.status === 'VersionMismatch'
              ? 'bg-surface-warning'
              : 'bg-surface-error',
      },
    };
  }

  return {
    iconName: 'electric-car-2',
    iconColor: IconColor.Primary,
    tag: undefined,
  };
};

export const LogItemTitle = ({
  log,
}: {
  log: OcppLogMessageFormatted;
}): React.JSX.Element => {
  const { t } = useTranslation();
  const { message } = log;
  const { iconName, iconColor, tag } = useMemo(
    () =>
      getLogDisplayInfo(
        log,
        t('$.charge-point-details.maintenance.log.no-info'),
      ),
    [log, t],
  );

  return (
    <Stack flex={1} gap={GapEnum.S} justifyContent="space-between">
      <Stack gap={GapEnum.S} alignItems="center" flex={1}>
        <Text color={TextColor.Secondary} fontSize={TextFontSize.Small}>
          {formatDate(new Date(log.message.time), i18n, 'dd/MM/yy - HH:mm')}
        </Text>
        <Icon name={iconName} color={iconColor} />
        <Text bold>
          {isOcppLogCallResultOrErrorMessage(message) ? message.method : '-'}
        </Text>
      </Stack>
      <div className={css.tags}>
        {tag && (
          <Tag className={tag.className} flex={1}>
            <Text fontSize={TextFontSize.Small} italic>
              {tag.label}
            </Text>
          </Tag>
        )}
        <Tag
          flex={1}
          className={classNames('bg-surface-background', css.messageId)}>
          <Text fontSize={TextFontSize.Small} italic truncate>
            {log.message.messageId}
          </Text>
        </Tag>
      </div>
    </Stack>
  );
};
